/**
 * Pathway layout service (PROD-591).
 *
 * Pure layout function for the React Flow canvas. The Phase 1 build does
 * NOT add a new npm dep; neither `dagre` nor `elkjs` are in `package.json`,
 * and the master prompt v3.3 §8 layout requirements (left to right
 * hierarchical flow with node ranks: Demographics, Vitals, Labs, Decision,
 * NBA, Outcome) are simple enough to hand-roll.
 *
 * The hand-rolled layout is hierarchical-grid:
 *   1. Each node carries a `nodeType`. The type maps to a column rank
 *      (0..N) per `RANK_BY_TYPE`. Demographics is column 0, Outcome is
 *      column N. Within a column nodes stack vertically.
 *   2. The layout returns `{ nodes, edges }` shaped for React Flow with
 *      explicit `position: { x, y }`. The geometry is deterministic so
 *      a given pathway always lays out the same way (test-friendly).
 *   3. Edges are returned untouched. React Flow handles routing.
 *
 * Why hand-rolled (not dagre): the deliverable forbids new npm deps. A
 * hierarchical-grid layout matches the master prompt v3.3 §8 spec and
 * keeps the bundle small. If the demo eventually needs a true graph
 * layout (cycles, branching) the trade-off is documented here so a
 * future ticket can swap in dagre with one import.
 *
 * HC5: zero em / en dashes. Section separators use `-` (hyphen), commas,
 * or "and" / "to".
 */
import type { Edge, Node } from 'reactflow';

/**
 * Closed vocabulary of node types.
 *
 * Two layers of vocabulary live here:
 *
 *   1. The 6 **activity types** (master prompt v3.3 §8): Demographics,
 *      Vitals, Labs, Decision Point, NBA, Outcome. These remain the
 *      legacy vocabulary used by the FHIR mapper and the existing
 *      Cardiometabolic / HRT / Metabolic Syndrome / CVD Prevention
 *      fixtures.
 *   2. The 3-tier **hierarchy** (Awell design ontology, Sprint 4.3.1
 *      / PROD-650): `track`, `step`, `action`. Tracks are the outermost
 *      declarative containers activated by triggers; Steps live inside
 *      a Track and are connected by transitions; Actions are the most
 *      granular building block and live inside a Step. See
 *      `awell/studio/docs/getting-started/design-ontology.md` (vault
 *      mirror at `03-knowledge-base/external-references/awell/studio/
 *      docs/getting-started/design-ontology.md`).
 *
 * Sprint 4.3.1 keeps the 6 activity types in the closed vocabulary so
 * existing fixtures + FHIR round-tripping continue to work; new
 * pathways gain the 3-tier hierarchy by composing `track` + `step` +
 * `action` nodes whose `category: 'user' | 'system'` discriminates the
 * action shape per the ontology.
 *
 * The schema is enforced at the pathway-store boundary; this service
 * trusts the input.
 */

/** Awell 3-tier hierarchy. Outermost (`track`) to innermost (`action`). */
export const HIERARCHY_NODE_TYPES = ['track', 'step', 'action'] as const;
export type HierarchyNodeType = (typeof HIERARCHY_NODE_TYPES)[number];

/** Legacy 6 activity types (master prompt v3.3 §8). */
export const ACTIVITY_NODE_TYPES = [
  'demographics',
  'vitals',
  'labs',
  'decision',
  'nba',
  'outcome',
] as const;
export type ActivityNodeType = (typeof ACTIVITY_NODE_TYPES)[number];

/**
 * Full closed vocabulary surfaced to the editor: hierarchy types come
 * first so the palette renders them at the top per Awell ontology.
 */
export const PATHWAY_NODE_TYPES = [
  ...HIERARCHY_NODE_TYPES,
  ...ACTIVITY_NODE_TYPES,
] as const;

export type PathwayNodeType = (typeof PATHWAY_NODE_TYPES)[number];

/**
 * Column rank used to lay nodes out left to right. Hierarchy nodes
 * carry `-1` so they do not collide with the legacy activity columns;
 * the canvas filters by hierarchy level (see `HIERARCHY_LEVELS`) before
 * positioning so the rank is only consulted for activity nodes.
 */
export const RANK_BY_TYPE: Record<PathwayNodeType, number> = {
  // Hierarchy nodes are positioned by `parentNodeId` rather than rank.
  track: 0,
  step: 0,
  action: 0,
  // Activity nodes keep their existing left-to-right rank.
  demographics: 0,
  vitals: 1,
  labs: 2,
  decision: 3,
  nba: 4,
  outcome: 5,
};

/**
 * Drill-down levels surfaced by the canvas. The editor renders one
 * level at a time per the Awell ontology drill-down: outermost shows
 * Tracks, mid shows Steps within the focused Track, innermost shows
 * Actions within the focused Step.
 */
export const HIERARCHY_LEVELS = ['tracks', 'steps', 'actions'] as const;
export type HierarchyLevel = (typeof HIERARCHY_LEVELS)[number];

/**
 * Node-type discriminator for the action category (user vs system) per
 * Awell ontology. Surfaced in the inspector + on the action node icon.
 */
export const ACTION_CATEGORIES = ['user', 'system'] as const;
export type ActionCategory = (typeof ACTION_CATEGORIES)[number];

/**
 * Closed vocabulary of Step categories surfaced as the StepCategoryBadge
 * pill on the canvas (Sprint 4.3.5). Mirrors the Awell Studio canvas
 * reference where each step card carries a coloured BASELINE / 16 WEEK
 * PROGRAM / EXCLUSION ... pill. The colour resolution belongs to the
 * `StepCategoryBadge` component (Signal DS tokens only); this vocab is
 * the data layer so fixtures + tests share a single source of truth.
 */
export const STEP_CATEGORIES = [
  'baseline',
  'program',
  'exclusion',
  'completion',
  'review',
  'other',
] as const;
export type StepCategory = (typeof STEP_CATEGORIES)[number];

/** Horizontal spacing between columns in CSS pixels. */
export const COLUMN_SPACING = 240;

/** Vertical spacing between nodes in the same column in CSS pixels. */
export const ROW_SPACING = 120;

/** Top padding so the first row is not flush against the canvas edge. */
export const TOP_PADDING = 40;

/** Left padding so the first column is not flush against the canvas edge. */
export const LEFT_PADDING = 40;

export interface PathwayLayoutNodeInput {
  id: string;
  nodeType: PathwayNodeType;
  label: string;
  /**
   * Optional metadata payload that React Flow surfaces as `data`. The
   * inspector reads this back when the node is selected.
   */
  data?: Record<string, unknown>;
}

export interface PathwayLayoutEdgeInput {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface PathwayLayoutInput {
  nodes: ReadonlyArray<PathwayLayoutNodeInput>;
  edges: ReadonlyArray<PathwayLayoutEdgeInput>;
}

export interface PathwayLayoutResult {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Layout a pathway into React Flow nodes + edges. Pure function. The
 * returned `position.x` is `LEFT_PADDING + RANK_BY_TYPE[nodeType] *
 * COLUMN_SPACING`; `position.y` is `TOP_PADDING + columnIndex *
 * ROW_SPACING` where `columnIndex` is the within-column ordinal for
 * the node's column.
 *
 * The layout is stable in input order: two consecutive nodes of the
 * same type land in adjacent rows of the same column.
 */
export function layoutPathway(input: PathwayLayoutInput): PathwayLayoutResult {
  // Sprint 4.3.7-fix-2: when the input mixes hierarchy nodes (track /
  // step / action) with legacy activity nodes (demographics, vitals,
  // labs, etc.), the legacy `RANK_BY_TYPE` collapses both groups into
  // column 0 and they collide at (LEFT_PADDING, TOP_PADDING). We detect
  // the mix and stack hierarchy nodes BELOW the legacy column block in
  // a separate row band so the user can still see + click both groups.
  // This is a transitional layout: a fully-migrated pathway has only
  // hierarchy nodes (no legacy), and a fully-legacy pathway has only
  // activity nodes (no hierarchy), so the offset path is dormant in the
  // common case.
  const HIERARCHY_SET = new Set<PathwayNodeType>([
    'track',
    'step',
    'action',
  ]);
  const hasHierarchy = input.nodes.some((n) => HIERARCHY_SET.has(n.nodeType));
  const hasLegacy = input.nodes.some((n) => !HIERARCHY_SET.has(n.nodeType));
  const mixed = hasHierarchy && hasLegacy;
  // Compute the legacy column-block height so hierarchy nodes can stack
  // below it without overlap.
  let legacyMaxRows = 0;
  if (mixed) {
    const counts: Record<string, number> = {};
    for (const n of input.nodes) {
      if (!HIERARCHY_SET.has(n.nodeType)) {
        counts[n.nodeType] = (counts[n.nodeType] ?? 0) + 1;
      }
    }
    for (const c of Object.values(counts)) {
      if (c > legacyMaxRows) legacyMaxRows = c;
    }
  }
  const HIERARCHY_BAND_OFFSET = mixed
    ? TOP_PADDING + legacyMaxRows * ROW_SPACING + ROW_SPACING
    : 0;

  const columnCounts: Record<string, number> = {};
  // Sprint 4.3.7-fix-2: hierarchy nodes (track / step / action) all
  // share rank 0 in `RANK_BY_TYPE` because their position is meant to
  // come from the parent-child relationship, not a left-to-right rank.
  // Stacking them vertically as a single column makes a Track + an
  // orphan Step (or several Steps inside a Track at the 'steps' drill-
  // down) read like the Awell Studio canvas reference. Use a shared
  // hierarchy counter so each hierarchy node lands on its own row.
  let hierarchyRow = 0;
  const positionedNodes: Node[] = input.nodes.map((node) => {
    const isHierarchy = HIERARCHY_SET.has(node.nodeType);
    const rank = RANK_BY_TYPE[node.nodeType];
    const x = LEFT_PADDING + rank * COLUMN_SPACING;
    let y: number;
    if (isHierarchy) {
      const within = hierarchyRow;
      hierarchyRow += 1;
      y =
        TOP_PADDING +
        within * ROW_SPACING +
        (mixed ? HIERARCHY_BAND_OFFSET : 0);
    } else {
      const within = columnCounts[node.nodeType] ?? 0;
      columnCounts[node.nodeType] = within + 1;
      y = TOP_PADDING + within * ROW_SPACING;
    }
    return {
      id: node.id,
      type: 'default',
      data: {
        label: node.label,
        nodeType: node.nodeType,
        ...(node.data ?? {}),
      },
      position: { x, y },
    };
  });
  const wiredEdges: Edge[] = input.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    type: 'default',
  }));
  return { nodes: positionedNodes, edges: wiredEdges };
}

/**
 * Convenience: produce a stable column rank for a given node type. Used
 * by the inspector + the list fallback to order rows the same way the
 * canvas orders them.
 */
export function rankFor(nodeType: PathwayNodeType): number {
  return RANK_BY_TYPE[nodeType];
}
